import { ChangeDetectorRef, Component, ElementRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import { WellHealthinessService } from 'app/modules/well-healthiness/services/well-healthiness.service';
import module from 'app/modules/well-healthiness/well-healthiness.module';
import { EMPTY, map, merge, Subject, switchMap, takeUntil } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import { MatDialog } from '@angular/material/dialog';
import { ViewCmComponent } from '../../components/view-cm/view-cm.component';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';
import { EditWellHealthinessComponent } from '../../components/edit-well-healthiness/edit-well-healthiness.component';
import { UserService } from 'app/core/user/user.service';
import { WellHealthinessHistoryComponent } from '../../components/wh-history/wh-history.component';

@Component({
    selector: 'app-well-healthiness',
    templateUrl: './index.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./index.page.scss'],
})
export class IndexPage {
    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild(MatSort) private _sort: MatSort;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    searchForm: UntypedFormGroup;
    dt: any
    startingIndex: number = 0
    isLoading: boolean = false

    sortInfo: string;
    private columnLabels: { [key: string]: string } = {
        ...DEFAULT_COLUMN_LABELS,
        wellHealthinessStatus: ' Well Healthiness Status',
        riskRanking: 'Risk Ranking',
        updateDate: 'Status Updated At',
        updateBy: 'Status Updated By',
    };

    columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
        key,
        name,
        isChecked: false, // default unchecked
    }));

    constructor(
        public _formBuilder: UntypedFormBuilder,
        public service: WellHealthinessService,
        private _changeDetectorRef: ChangeDetectorRef,
        private dialog: MatDialog,
        private userService: UserService
    ) { }


    ngOnInit() {
        this.setSearch();
        this.setDataTable();
        this.setPage({ offset: 0 });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort
            this._sort.sort({
                id: 'field',
                start: 'asc',
                disableClear: true,
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;

                    this.onSort(this._sort)
                });

            // Get platform types if sort or page changes
            merge(this._sort.sortChange, this._paginator.page).pipe(
                switchMap(() => {
                    // this.isLoading = true;

                    let pageInfo = {
                        'count': this._paginator.length,
                        'pageSize': this._paginator.pageSize,
                        'limit': this._paginator.pageSize,
                        'offset': this._paginator.pageIndex
                    }
                    this.setPage(pageInfo); // Emit page number

                    return EMPTY
                }),
                map(() => {
                    this.isLoading = false;
                }),
            ).subscribe();
        }
    }

    onSort(sort: any) {
        if (!sort) return;
        this.searchForm.patchValue({
            OrderBy1: sort.active,
            OrderDir: sort.direction,
        });

        this.sortInfo = this.columnLabels[sort.active] || sort.active;
        this.setPage({ offset: 0 });
    }

    setPage(pageInfo) {
        this.dt.page.offset = pageInfo.offset;
        this.startingIndex = this.dt.page.offset * this.dt.page.size

        this.searchForm.patchValue({
            Page: this.dt.page.offset + 1,
        });

        const params = this.searchForm.value;

        const search: any = {
            wellLongName: params.Well?.wellLongName,
            stringShortName: params.String?.stringShortName,
            platformLongName: params.Platform?.platformLongName,
            fieldLongName: params.Field?.fieldLongName
        };

        this.service
            .getHealthinessTable({ ...params, search })
            .subscribe((pagedData) => {
                this.dt.page.totalPages = pagedData.data.totalPages;
                this.dt.page.totalRecords = pagedData.data.totalRecords;
                this.dt.rows = pagedData.data.data;
            });
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        }
    }

    handleSearch(event$) {
        const field = event$?.Field;
        const platform = event$?.Platform;
        const well = event$?.Well;
        const string = event$?.String;

        if ([field, platform, well, string].some(v => v != null && v !== '')) {
            this.setPage({ offset: 0 });
        }
    }

    setSearch() {
        this.sortInfo = 'Field'
        this.searchForm = this._formBuilder.group({
            Page: [0],
            PageSize: [20],
            OrderBy1: [null],
            OrderBy2: [null],
            OrderBy3: [null],
            OrderDir: 'asc',
            WellName: [''],
            PlatformName: [''],
            StringName: [''],
            FieldName: [''],
        });

        this.searchForm.get('PageSize').valueChanges.subscribe(value => this.setSize(value));
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0
        this.setPage({ offset: this.dt.page.offset });
    }

    reset() {
        this.setSearch();
        this.setPage({ offset: 0 });
    }

    handleFormatDate(dateString: string, withTime: boolean = true) {
        return handleFormatDate(dateString, withTime)
    }

    downloadWellHelthiness() {
        this.service.downloadWellHealthiness()
    }

    viewCm(wellString) {
        const dialogRef = this.dialog.open(ViewCmComponent, {
            width: '70%',
            data: wellString
        });
    }

    editWellHealthiness(wellString) {
        const dialogRef = this.dialog.open(EditWellHealthinessComponent, {
            width: '90%',
            data: wellString
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result.status == 'success')
                this.setPage({ offset: this.dt.page.offset })
        })
    }

    viewWellHealthinessHistory(wellString) {
        const dialogRef = this.dialog.open(WellHealthinessHistoryComponent, {
            width: '90%',
            data: wellString
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result.status == 'success')
                this.setPage({ offset: this.dt.page.offset })
        })
    }

    applySort(payload: ApplySortPayload) {
        // Patch form (fill missing with null)
        const [OrderBy1, OrderBy2, OrderBy3] = [
            payload.columns[0]?.key ?? null,
            payload.columns[1]?.key ?? null,
            payload.columns[2]?.key ?? null,
        ];

        this.searchForm.patchValue({
            OrderBy1,
            OrderBy2,
            OrderBy3,
            OrderDir: payload.orderDir ?? 'asc',
        });

        const labels = payload.columns.map(k => k.name);
        this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
        this.setPage({ offset: 0 });
    }

    hasWellHealthinessEditRoles(): boolean {
        // roles that can edit cdft
        let hasRole = false;

        const roles = new Set([
            'Well Integrity Senior Engineer',
        ]);

        this.userService.roles$
            .subscribe((userRoles) => {
                hasRole = userRoles.some((r) => roles.has(r));
            })
            .unsubscribe();
        return hasRole;
    }
}
